import React, { useState, useEffect } from 'react';
import { contractorsAPI, tradeAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, Edit, Trash2, Phone, Mail, Users, MapPin, X, Send, Loader2, CheckCircle2, Wand2, Upload, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const tradeTypes = [
  'General', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Tiler', 
  'Roofer', 'Plasterer', 'Bricklayer', 'Concreter', 'Landscaper',
  'HVAC', 'Glazier', 'Fencer', 'Flooring', 'Other'
];

const qldRegions = ['Brisbane', 'Gold Coast', 'Wide Bay'];

export const ContractorsPage = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    trade_types: [],
    regions: [],
    email: '',
    phone: '',
    company_name: '',
    address: '',
    hourly_rate: '',
    notes: '',
  });

  // Invite Trade State
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteData, setInviteData] = useState({
    name: '', email: '', trade_types: [], message: ''
  });

  // Legacy state - keeping for compatibility
  const [autoOnboardOpen, setAutoOnboardOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardData, setOnboardData] = useState({
    name: '', email: '', phone: '', company_name: '', trade_types: [], regions: [], address: '',
  });
  const [documents, setDocuments] = useState({
    qbcc_licence: null, public_liability: null, workcover: null, professional_indemnity: null,
  });
  const [validationResults, setValidationResults] = useState(null);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await contractorsAPI.getAll();
      setContractors(response.data);
    } catch (error) {
      toast.error('Failed to fetch contractors');
    } finally {
      setLoading(false);
    }
  };

  const handleTradeToggle = (trade) => {
    setFormData(prev => ({
      ...prev,
      trade_types: prev.trade_types.includes(trade)
        ? prev.trade_types.filter(t => t !== trade)
        : [...prev.trade_types, trade]
    }));
  };

  const handleRegionToggle = (region) => {
    setFormData(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.trade_types.length === 0) {
      toast.error('Please select at least one trade type');
      return;
    }
    try {
      const dataToSubmit = {
        ...formData,
        trade_type: formData.trade_types[0],
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      };
      
      if (editingContractor) {
        await contractorsAPI.update(editingContractor.id, dataToSubmit);
        toast.success('Contractor updated');
      } else {
        await contractorsAPI.create(dataToSubmit);
        toast.success('Contractor created');
      }
      resetForm();
      fetchContractors();
    } catch (error) {
      toast.error(editingContractor ? 'Failed to update' : 'Failed to create');
    }
  };

  const handleEdit = (contractor) => {
    setEditingContractor(contractor);
    setFormData({
      name: contractor.name || '',
      trade_types: contractor.trade_types || (contractor.trade_type ? [contractor.trade_type] : []),
      regions: contractor.regions || [],
      email: contractor.email || '',
      phone: contractor.phone || '',
      company_name: contractor.company_name || '',
      address: contractor.address || '',
      hourly_rate: contractor.hourly_rate || '',
      notes: contractor.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contractor?')) return;
    try {
      await contractorsAPI.delete(id);
      toast.success('Contractor deleted');
      fetchContractors();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingContractor(null);
    setFormData({
      name: '', trade_types: [], regions: [], email: '', phone: '',
      company_name: '', address: '', hourly_rate: '', notes: '',
    });
  };

  // Auto Onboard Functions
  const handleDocumentUpload = (docType, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setDocuments(prev => ({ ...prev, [docType]: { file, base64, filename: file.name } }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAutoOnboard = async () => {
    if (!onboardData.name || !onboardData.email || !onboardData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (onboardData.trade_types.length === 0) {
      toast.error('Please select at least one trade type');
      return;
    }
    if (!documents.qbcc_licence || !documents.public_liability || !documents.workcover) {
      toast.error('Please upload all required documents');
      return;
    }

    setOnboardLoading(true);
    try {
      const docList = Object.entries(documents)
        .filter(([_, doc]) => doc !== null)
        .map(([docType, doc]) => ({
          document_type: docType,
          file_data: doc.base64,
          filename: doc.filename
        }));

      const response = await api.post('/contractors/auto-onboard', {
        ...onboardData,
        documents: docList
      });

      setValidationResults(response.data);
      
      if (response.data.status === 'success') {
        toast.success('Contractor onboarded successfully!');
        fetchContractors();
      } else if (response.data.status === 'pending_review') {
        toast.warning('Contractor created but some documents need review');
        fetchContractors();
      }
      setOnboardingStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto onboarding failed');
    } finally {
      setOnboardLoading(false);
    }
  };

  const resetAutoOnboard = () => {
    setAutoOnboardOpen(false);
    setOnboardingStep(1);
    setOnboardData({ name: '', email: '', phone: '', company_name: '', trade_types: [], regions: [], address: '' });
    setDocuments({ qbcc_licence: null, public_liability: null, workcover: null, professional_indemnity: null });
    setValidationResults(null);
  };

  // Invite Trade Functions
  const handleInviteTradeToggle = (trade) => {
    setInviteData(prev => ({
      ...prev,
      trade_types: prev.trade_types.includes(trade)
        ? prev.trade_types.filter(t => t !== trade)
        : [...prev.trade_types, trade]
    }));
  };

  const handleSendInvite = async () => {
    if (!inviteData.name || !inviteData.email) {
      toast.error('Name and email are required');
      return;
    }
    
    setInviteLoading(true);
    try {
      await api.post('/contractors/invite', inviteData);
      toast.success('Invitation sent successfully!');
      setInviteSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const resetInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteSent(false);
    setInviteData({ name: '', email: '', trade_types: [], message: '' });
  };

  const getContractorTrades = (contractor) => {
    if (contractor.trade_types && contractor.trade_types.length > 0) return contractor.trade_types;
    return contractor.trade_type ? [contractor.trade_type] : [];
  };

  const filteredContractors = contractors.filter(c => {
    const trades = getContractorTrades(c);
    return (
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trades.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.regions || []).some(r => r.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Trades</h1>
          <p className="text-muted-foreground mt-1">Manage your trades and crew</p>
        </div>
        <div className="flex gap-2">
          {/* Invite Trade Button */}
          <Dialog open={inviteDialogOpen} onOpenChange={(open) => { if (!open) resetInviteDialog(); else setInviteDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground" data-testid="invite-trade-btn">
                <Send className="h-4 w-4 mr-2" />
                Invite Trade
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-primary" />
                  Invite Trade to Register
                </DialogTitle>
              </DialogHeader>
              
              {!inviteSent ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Send an email invitation to a trade. They'll receive a link to fill in their details and upload their documents.
                  </p>
                  
                  <div className="space-y-2">
                    <Label>Trade Name *</Label>
                    <Input 
                      value={inviteData.name} 
                      onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                      placeholder="e.g., Mike Smith" 
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={inviteData.email} 
                      onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                      placeholder="e.g., mike@plumbing.com" 
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Trade Types (optional)</Label>
                    <div className="grid grid-cols-3 gap-2 p-3 bg-input/50 rounded-lg border border-border max-h-40 overflow-y-auto">
                      {tradeTypes.map(trade => (
                        <div key={trade} className="flex items-center space-x-2">
                          <Checkbox 
                            id={`invite-trade-${trade}`} 
                            checked={inviteData.trade_types.includes(trade)} 
                            onCheckedChange={() => handleInviteTradeToggle(trade)} 
                          />
                          <label htmlFor={`invite-trade-${trade}`} className="text-sm cursor-pointer">{trade}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Personal Message (optional)</Label>
                    <Textarea 
                      value={inviteData.message} 
                      onChange={(e) => setInviteData({...inviteData, message: e.target.value})}
                      placeholder="Add a personal note to your invitation..."
                      rows={3}
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={resetInviteDialog}>Cancel</Button>
                    <Button onClick={handleSendInvite} disabled={inviteLoading}>
                      {inviteLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" /> Send Invitation</>
                      )}
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Invitation Sent!</h3>
                  <p className="text-muted-foreground mb-4">
                    An email has been sent to <strong>{inviteData.email}</strong> with instructions to complete their registration.
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    The invitation is valid for 7 days. You'll be notified when they complete their registration.
                  </p>
                  <Button onClick={resetInviteDialog}>Done</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Legacy Auto Onboard Button - Hidden */}
          <Dialog open={autoOnboardOpen} onOpenChange={(open) => { if (!open) resetAutoOnboard(); else setAutoOnboardOpen(true); }}>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto hidden">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-primary" />
                  AI Auto Onboard Contractor
                </DialogTitle>
              </DialogHeader>
              
              {/* Step Indicator */}
              <div className="flex items-center justify-center gap-2 py-4">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${onboardingStep >= step ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
                      {onboardingStep > step ? <CheckCircle2 className="h-5 w-5" /> : step}
                    </div>
                    {step < 3 && <div className={`w-12 h-1 ${onboardingStep > step ? 'bg-primary' : 'bg-secondary'}`} />}
                  </div>
                ))}
              </div>

              {/* Step 1: Basic Details */}
              {onboardingStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input value={onboardData.name} onChange={(e) => setOnboardData({ ...onboardData, name: e.target.value })} className="bg-input border-border rounded-lg" placeholder="John Smith" />
                    </div>
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input value={onboardData.company_name} onChange={(e) => setOnboardData({ ...onboardData, company_name: e.target.value })} className="bg-input border-border rounded-lg" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input type="email" value={onboardData.email} onChange={(e) => setOnboardData({ ...onboardData, email: e.target.value })} className="bg-input border-border rounded-lg" />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone *</Label>
                      <Input value={onboardData.phone} onChange={(e) => setOnboardData({ ...onboardData, phone: e.target.value })} className="bg-input border-border rounded-lg" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Trade Types *</Label>
                    <div className="grid grid-cols-4 gap-2 p-3 bg-input/50 rounded-lg border border-border">
                      {tradeTypes.map(trade => (
                        <div key={trade} className="flex items-center space-x-2">
                          <Checkbox id={`ob-trade-${trade}`} checked={onboardData.trade_types.includes(trade)} onCheckedChange={() => setOnboardData(prev => ({ ...prev, trade_types: prev.trade_types.includes(trade) ? prev.trade_types.filter(t => t !== trade) : [...prev.trade_types, trade] }))} />
                          <label htmlFor={`ob-trade-${trade}`} className="text-sm cursor-pointer">{trade}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Regions</Label>
                    <div className="flex gap-4 p-3 bg-input/50 rounded-lg border border-border">
                      {qldRegions.map(region => (
                        <div key={region} className="flex items-center space-x-2">
                          <Checkbox id={`ob-region-${region}`} checked={onboardData.regions.includes(region)} onCheckedChange={() => setOnboardData(prev => ({ ...prev, regions: prev.regions.includes(region) ? prev.regions.filter(r => r !== region) : [...prev.regions, region] }))} />
                          <label htmlFor={`ob-region-${region}`} className="text-sm cursor-pointer">{region}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={resetAutoOnboard}>Cancel</Button>
                    <Button onClick={() => {
                      if (!onboardData.name || !onboardData.email || !onboardData.phone || onboardData.trade_types.length === 0) {
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      setOnboardingStep(2);
                    }}>Next: Upload Documents</Button>
                  </DialogFooter>
                </div>
              )}

              {/* Step 2: Document Upload */}
              {onboardingStep === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Upload documents for AI verification. Images work best.</p>
                  {[
                    { key: 'qbcc_licence', label: 'QBCC Licence', required: true },
                    { key: 'public_liability', label: 'Public Liability Insurance', required: true },
                    { key: 'workcover', label: 'WorkCover Certificate', required: true },
                    { key: 'professional_indemnity', label: 'Professional Indemnity', required: false },
                  ].map(({ key, label, required }) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-input/50 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        {documents[key] ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
                        <div>
                          <p className="font-medium">{label} {required && <span className="text-destructive">*</span>}</p>
                          {documents[key] && <p className="text-xs text-muted-foreground">{documents[key].filename}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {documents[key] && <Button variant="ghost" size="sm" onClick={() => setDocuments(prev => ({ ...prev, [key]: null }))}><X className="h-4 w-4" /></Button>}
                        <label className="cursor-pointer">
                          <Input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => handleDocumentUpload(key, e.target.files[0])} />
                          <span className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium bg-secondary hover:bg-secondary/80 rounded-lg">{documents[key] ? 'Replace' : 'Upload'}</span>
                        </label>
                      </div>
                    </div>
                  ))}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOnboardingStep(1)}>Back</Button>
                    <Button onClick={handleAutoOnboard} disabled={onboardLoading}>
                      {onboardLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Validating...</> : <><Wand2 className="h-4 w-4 mr-2" />AI Validate & Onboard</>}
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* Step 3: Results */}
              {onboardingStep === 3 && validationResults && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${validationResults.status === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                    <div className="flex items-center gap-2">
                      {validationResults.status === 'success' ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <AlertCircle className="h-5 w-5 text-yellow-500" />}
                      <span className="font-semibold">{validationResults.message}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold">Document Validation Results:</h4>
                    {validationResults.document_results?.map((result, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-input/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {result.status === 'valid' ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-yellow-500" />}
                          <span className="capitalize">{result.document_type.replace(/_/g, ' ')}</span>
                        </div>
                        <Badge variant={result.status === 'valid' ? 'default' : 'secondary'}>{result.status.replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button onClick={resetAutoOnboard}>Done</Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Add Contractor Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="font-semibold rounded-lg" data-testid="add-contractor-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Contractor
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingContractor ? 'Edit Contractor' : 'Add Contractor'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-input border-border rounded-lg" data-testid="contractor-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="bg-input border-border rounded-lg" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Trade Types *</Label>
                  <div className="grid grid-cols-4 gap-2 p-3 bg-input/50 rounded-lg border border-border">
                    {tradeTypes.map(trade => (
                      <div key={trade} className="flex items-center space-x-2">
                        <Checkbox id={`trade-${trade}`} checked={formData.trade_types.includes(trade)} onCheckedChange={() => handleTradeToggle(trade)} />
                        <label htmlFor={`trade-${trade}`} className="text-sm cursor-pointer">{trade}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Regions</Label>
                  <div className="flex gap-4 p-3 bg-input/50 rounded-lg border border-border">
                    {qldRegions.map(region => (
                      <div key={region} className="flex items-center space-x-2">
                        <Checkbox id={`region-${region}`} checked={formData.regions.includes(region)} onCheckedChange={() => handleRegionToggle(region)} />
                        <label htmlFor={`region-${region}`} className="text-sm cursor-pointer">{region}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="bg-input border-border rounded-lg" data-testid="contractor-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="bg-input border-border rounded-lg" data-testid="contractor-phone" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-input border-border rounded-lg" />
                  </div>
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input type="number" value={formData.hourly_rate} onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })} className="bg-input border-border rounded-lg" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="bg-input border-border rounded-lg" rows={3} />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                  <Button type="submit" className="font-semibold rounded-lg" data-testid="contractor-submit">{editingContractor ? 'Update' : 'Create'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search contractors..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-input border-border rounded-lg" data-testid="contractor-search" />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredContractors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Users className="h-12 w-12 mb-4" />
              <p>No contractors found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Name</TableHead>
                  <TableHead>Trades</TableHead>
                  <TableHead>Regions</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContractors.map((contractor) => {
                  const trades = getContractorTrades(contractor);
                  return (
                    <TableRow key={contractor.id} className="border-border" data-testid={`contractor-row-${contractor.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{contractor.name}</p>
                          {contractor.company_name && <p className="text-xs text-muted-foreground">{contractor.company_name}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {trades.map(trade => (
                            <Badge key={trade} variant="outline" className="rounded-lg font-mono text-xs uppercase">{trade}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(contractor.regions || []).map(region => (
                            <Badge key={region} variant="secondary" className="rounded-lg text-xs"><MapPin className="h-3 w-3 mr-1" />{region}</Badge>
                          ))}
                          {(!contractor.regions || contractor.regions.length === 0) && <span className="text-muted-foreground text-xs">-</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <a href={`mailto:${contractor.email}`} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                            <Mail className="h-3 w-3" />{contractor.email}
                          </a>
                          <a href={`tel:${contractor.phone}`} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                            <Phone className="h-3 w-3" />{contractor.phone}
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        {contractor.hourly_rate ? (
                          <span className="font-mono">${contractor.hourly_rate}/hr</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(contractor)} data-testid={`edit-contractor-${contractor.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(contractor.id)} className="text-destructive" data-testid={`delete-contractor-${contractor.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractorsPage;
